import React, {useCallback} from 'react';
import styled from 'styled-components';
import {Icon} from 'shared/helpers';
import {Face} from 'components/customer/Product/Drawer/helpers/componentFaces';
import {FaceHeightField} from 'components/customer/Product/Drawer/FaceHeightField';
import {
    Badge,
    Heading,
    Panel,
    ResetButton,
} from 'components/customer/Product/Drawer/layoutStyles';

interface SelectedFaceProps {
    faces: Face[];
    fieldName: string;
    selectedIndex: number;
    onSetHeight: (index: number, height: number) => void;
    onSetAuto: (index: number) => void;
}

const hintFor = (face: Face) => {
    if (face.isLocked) {
        return 'One face has to stay on auto to take up whatever is left over.';
    }

    return face.isAuto
        ? 'Sharing what the set faces leave behind.'
        : 'Set by hand. The faces on auto share what is left.';
};

export const SelectedFace = ({
    faces,
    fieldName,
    selectedIndex,
    onSetHeight,
    onSetAuto,
}: SelectedFaceProps) => {
    const autoHandler = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            onSetAuto(Number(event.currentTarget.dataset.index));
        },
        [onSetAuto]
    );

    const face = faces[Number(selectedIndex)];

    if (!face) return null;

    return (
        <Panel data-cy="selected-face">
            <Block data-cy={`selected-face-${face.index}`}>
                <Heading data-cy={`drawer-heading-${face.index}`}>
                    <Badge>{face.index + 1}</Badge>
                    <span>Drawer {face.index + 1}</span>
                </Heading>

                <FaceHeightField
                    index={face.index}
                    fieldName={fieldName}
                    value={face.height}
                    disabled={face.isLocked}
                    sharesDraft
                    onCommit={onSetHeight}
                    trailing={
                        <ResetButton
                            variant="link"
                            className="button-blue btn-sm"
                            data-index={face.index}
                            data-cy={`drawer-auto-${face.index}`}
                            onClick={autoHandler}
                            disabled={face.isAuto}>
                            <Icon
                                iconName="Reverse-Refresh.svg"
                                color="white"
                            />
                            <span>Auto</span>
                        </ResetButton>
                    }
                />

                <Hint data-cy={`drawer-face-hint-${face.index}`}>
                    {hintFor(face)}
                </Hint>
            </Block>
        </Panel>
    );
};

const Hint = styled.div`
    margin-top: 8px;
    color: #6b7480;
    font-size: 12px;
    text-wrap: pretty;
`;

const Block = styled.div`
    padding: 13px;

    /* FormGroup indents 10px, pushing fields out of line with the heading */
    > div:not(${Heading}) {
        padding-left: 0;
    }

    .row {
        margin-left: 0;
        margin-right: 0;
    }

    /* stack each field, label above the control, like the margins card */
    [class*='col-'] {
        flex: 0 0 100%;
        width: 100%;
        max-width: 100%;
        padding-left: 0;
        padding-right: 0;
    }
`;
